<template>
    <div>
        <div v-if="loading" class="text-center">
            <i class="fa fa-circle-o-notch fa-spin"></i> Loading
        </div>
        <div v-else>
            <vue-headful :title="(form.inspection_form_id ? form.form_name+' [Edit]' : 'New Inspection Form') + ' :: MeritEHS'" />

            <h4 class="py-3 mb-4">
                <ol class="breadcrumb font-weight-bold m-0 d-block d-md-inline-block">
                    <li class="breadcrumb-item font-weight-light d-block d-md-inline-block">
                        <router-link :to="`/inspections/forms`">
                            <i class="fa fa-clipboard"></i> Inspection Forms
                        </router-link>
                    </li>
                    <template v-if="form.inspection_form_id">
                        <li class="breadcrumb-item font-weight-light d-block d-md-inline-block mt-md-0 mt-2">
                            <router-link :to="`/inspections/forms/view/${form.inspection_form_id}`">
                                {{ form.form_name }}
                            </router-link>
                        </li>
                        <li class="breadcrumb-item active d-block d-md-inline-block mt-md-0 mt-2">Edit</li>
                    </template>
                    <template v-else>
                        <li class="breadcrumb-item active d-block d-md-inline-block mt-md-0 mt-2">New</li>
                    </template>
                </ol>
            </h4>

            <div class="row">
                <div class="col-xl-8">
                    <div class="card mb-3">
                        <div class="card-header">
                            <strong>Form Details</strong>
                            <div class="float-sm-right" v-show="false">
                                <b-button variant="success" size="sm" v-on:click="saveForm()" :disabled="saving">Save Form</b-button>
                            </div>
                        </div>
                        <div class="card-body">
                            <div class="row mb-3">
                                <div :class="{'col-lg-3': true, 'text-danger': errors.form_name}">Form Name: <i class="fa fa-asterisk text-danger small" v-if="!form.form_name"></i></div>
                                <div class="col-lg-8">
                                    <input v-model="form.form_name" :class="{'form-control': true, 'is-invalid': errors.form_name}" placeholder="Enter a Name for this Form" />
                                    <span v-if="errors.form_name" class="text-danger">
                                        <span v-for="error in errors.form_name">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="row mb-3">
                                <div class="col-lg-3">Active:</div>
                                <div class="col-lg-8">
                                    <b-form-checkbox v-model="form.is_active" plain :value="1" :unchecked-value="0"></b-form-checkbox>
                                </div>
                            </div>
                            <div class="row mb-3">
                                <div class="col-lg-3">Hide Equipment: <i class="fa fa-question-circle" title="If checked, the Superintendent field will be hidden and disabled"></i></div>
                                <div class="col-lg-8">
                                    <b-form-checkbox v-model="form.hide_equipment" plain :value="1" :unchecked-value="0"></b-form-checkbox>
                                </div>
                            </div>
                            <div class="row mb-3" v-if="form.hide_equipment == 0">
                                <div class="col-lg-3">Require Equipment:</div>
                                <div class="col-lg-8">
                                    <b-form-checkbox v-model="form.req_equipment" plain :value="1" :unchecked-value="0"></b-form-checkbox>
                                </div>
                            </div>

                            <template v-if="$store.state.user.enable_public_insp_forms == 1">
                                <div class="row mb-3">
                                    <div class="col-lg-3">Public Form: <i class="fa fa-question-circle" title="Use this to create Behavior Based Observation Forms (BBO)"></i></div>
                                    <div class="col-lg-8">
                                        <b-form-checkbox v-model="form.is_public_form" plain :value="1" :unchecked-value="0"></b-form-checkbox>
                                    </div>
                                </div>
                                <div class="row mb-3">
                                    <div class="col-lg-3">Hide Creator: <i class="fa fa-question-circle" title="If checked, only system Administrators will be able to see the creator of this form."></i></div>
                                    <div class="col-lg-8">
                                        <b-form-checkbox v-model="form.hide_creator" plain :value="1" :unchecked-value="0"></b-form-checkbox>
                                    </div>
                                </div>
                                <div class="row mb-3">
                                    <div class="col-lg-3">Hide Superintendent: <i class="fa fa-question-circle" title="If checked, the Superintendent field will be hidden and disabled"></i></div>
                                    <div class="col-lg-8">
                                        <b-form-checkbox v-model="form.hide_superintendent" plain :value="1" :unchecked-value="0"></b-form-checkbox>
                                    </div>
                                </div>
                                <div class="row mb-3" v-if="form.hide_superintendent == 0">
                                    <div class="col-lg-3">Require Superintendent: <i class="fa fa-question-circle" title="If checked, users will be required to enter a Superintendent."></i></div>
                                    <div class="col-lg-8">
                                        <b-form-checkbox v-model="form.req_superintendent" plain :value="1" :unchecked-value="0"></b-form-checkbox>
                                    </div>
                                </div>
                                <div class="row mb-3">
                                    <div class="col-lg-3">Require "# of Employees on Site": <i class="fa fa-question-circle" title="If checked, users will be required to enter the number of employees on site."></i></div>
                                    <div class="col-lg-8">
                                        <b-form-checkbox v-model="form.req_num_of_employees" plain :value="1" :unchecked-value="0"></b-form-checkbox>
                                    </div>
                                </div>
                                <div class="row mb-3">
                                    <div class="col-lg-3">Require Additional Files/Photos: <i class="fa fa-question-circle" title="If checked, users will be required to include additional files and photos."></i></div>
                                    <div class="col-lg-8">
                                        <b-form-checkbox v-model="form.req_add_files_and_photos" plain :value="1" :unchecked-value="0"></b-form-checkbox>
                                    </div>
                                </div>
                                <template v-if="form.is_public_form == 1">
                                    <div class="row mb-3">
                                        <div class="col-lg-3">
                                            Disable Edit Mode:&nbsp;
                                            <i class="fa fa-question-circle" title="If checked, employees accessing this form will only be able to create entries. They will not be able to view their list, nor edit past entries."></i>
                                        </div>
                                        <div class="col-lg-8">
                                            <b-form-checkbox v-model="form.disable_edit_mode" plain :value="1" :unchecked-value="0"></b-form-checkbox>
                                        </div>
                                    </div>
                                    <div class="row mb-3">
                                        <div class="col-lg-3">
                                            Enable File Uploads:&nbsp;
                                            <i class="fa fa-question-circle" title="If checked, employees can upload files to the inspection."></i>
                                        </div>
                                        <div class="col-lg-8">
                                            <b-form-checkbox v-model="form.enable_file_uploads" plain :value="1" :unchecked-value="0"></b-form-checkbox>
                                        </div>
                                    </div>
                                    <div class="row mb-3">
                                        <div class="col-lg-3">
                                            Enable Corrective Actions:&nbsp;
                                            <i class="fa fa-question-circle" title="If checked, employees can add corrective actions to Non-Compliant items."></i>
                                        </div>
                                        <div class="col-lg-8">
                                            <b-form-checkbox v-model="form.enable_corrective_actions" plain :value="1" :unchecked-value="0"></b-form-checkbox>
                                        </div>
                                    </div>
                                </template>
                            </template>

                            <template v-if="$store.state.user.enable_module_mic == 1 && form.is_public_form == 1">
                                <div class="row mb-3">
                                    <div class="col-lg-3">Include on MIC: <i class="fa fa-question-circle" title="Use this to add these entries to the MIC Participation Report"></i></div>
                                    <div class="col-lg-8">
                                        <b-form-checkbox v-model="form.add_to_hse_partic_export" plain :value="1" :unchecked-value="0"></b-form-checkbox>
                                    </div>
                                </div>
                            </template>
                            <div class="row mb-3" v-if="form.is_public_form !== 1 || form.enable_corrective_actions == 1">
                                <div class="col-lg-3">Require Corrective Action for {{ result_labels.noncompliant ? result_labels.noncompliant : 'Non-Compliant' }} Results:</div>
                                <div class="col-lg-8">
                                    <b-form-checkbox v-model="form.req_corrective_actions" plain :value="1" :unchecked-value="0"></b-form-checkbox>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="card mb-3">
                        <div class="card-header">
                            <strong>Result Answer Labels</strong>
                        </div>
                        <div class="card-body">
                            <div class="row mb-2">
                                <div class="col-lg-3 text-muted text-lg-right">Compliant:</div>
                                <div class="col-lg-6">
                                    <b-form-input v-model="result_labels.compliant" size="sm" placeholder="Enter a Label to Override the 'Compliant' Answer Label" />
                                </div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-lg-3 text-muted text-lg-right">Non-Compliant:</div>
                                <div class="col-lg-6">
                                    <b-form-input v-model="result_labels.noncompliant" size="sm" placeholder="Enter a Label to Override the 'Non-Compliant' Answer Label" />
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-lg-3 text-muted text-lg-right">N/A:</div>
                                <div class="col-lg-6">
                                    <b-form-input v-model="result_labels.na" size="sm" placeholder="Enter a Label to Override the 'N/A' Answer Label" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <personnel-edit ref="inspPersonnelFields" :personnel="personnel" />

                    <b-button variant="success" size="sm" v-on:click="saveForm()" :disabled="saving">Save Form</b-button>
                    <hr class="mb-4" />

                    <h4 class="mt-4">Inspection Sections <b-button variant="light" size="sm" v-on:click="newSection()" v-if="form.inspection_form_id"><span class="text-success"><i class="fa fa-plus-circle"></i> New Section</span></b-button></h4>
                    <template v-if="!form.inspection_form_id">
                        <div class="card mb-4">
                            <div class="card-body">
                                <div class="alert alert-info">Save the Inspection Form First to start adding sections and elements.</div>
                            </div>
                        </div>
                    </template>
                    <template v-else>
                        <div class="card mb-4" v-for="section in form.sections">
                            <div class="card-header">
                                <strong style="font-size: 14px;">{{ section.DisplayOrder }}. {{ section.SectionName }}</strong>

                                <label :class="{'badge': true, 'badge-success': section.IsActive == 1, 'badge-warning': section.IsActive != 1}">
                                    {{ section.IsActive == 1 ? 'Active' : 'Inactive' }}
                                </label>

                                <div class="float-sm-right">
                                    <button class="btn btn-sm btn-light" v-on:click="newElement(section.InspectionSectionId)" title="Add a New Section Element">
                                        <i class="fa fa-plus"></i> Item
                                    </button>
                                    <button class="btn btn-sm btn-light mr-1" v-on:click="editSection(section)" title="Edit Section Name">
                                        <i class="fa fa-edit"></i> Edit
                                    </button>
                                    <b-dropdown size="sm" variant="light" v-if="false">
                                        <template #button-content>
                                        Mark All Items <i class="fa fa-caret-down"></i>
                                        </template>
                                        <b-dropdown-item href="javascript:void(0)" v-on:click="markAll(section.InspectionSectionId, 1)">Active</b-dropdown-item>
                                        <b-dropdown-item href="javascript:void(0)" v-on:click="markAll(section.InspectionSectionId, 0)">Inactive</b-dropdown-item>
                                    </b-dropdown>
                                    <button class="btn btn-sm btn-light mr-1" v-on:click="deleteSection(section)" title="Delete Section" v-if="!form.published_at">
                                        <span class="text-danger"><i class="fa fa-trash"></i> Delete</span>
                                    </button>
                                </div>
                            </div>
                            <div class="card-body">
                                <div class="mb-3" v-for="element in section.elements">
                                    <div>
                                        <button class="btn btn-xs btn-link" v-on:click="editElement(element)">
                                            <i class="fa fa-edit"></i>
                                        </button>
                                        <button class="btn btn-xs btn-link text-danger" v-on:click="deleteElement(element)" v-if="!form.published_at">
                                            <i class="fa fa-trash"></i>
                                        </button>
                                        <strong>{{ section.DisplayOrder }}.{{ element.DisplayOrder }}</strong>
                                        <span class="text-info">({{ element.element_score }} pts)</span>
                                        <label :class="{'badge': true, 'badge-success': element.IsActive == 1, 'badge-warning': element.IsActive != 1}" v-if="false">
                                            {{ element.IsActive == 1 ? 'Active' : 'Inactive' }}
                                        </label>
                                    </div>
                                    {{ element.ElementText }}
                                    <div v-if="element.Description || element.osha_standard_url" class="text-muted" style="margin-left: 35px;">
                                        <div v-if="element.Description">{{ element.Description }}</div>
                                        <div v-if="element.osha_standard_url">
                                            <a :href="element.osha_standard_url" target="_blank" title="View OSHA Standard">{{ element.osha_standard_url }}</a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <form-section-modal ref="sectionModal" @sectionSaved="sectionSaved" />
                        <form-element-modal ref="elementModal" @elementSaved="elementSaved" />
                    </template>

                    <h4 class="mt-4">Custom Fields</h4>
                    <template v-if="!form.inspection_form_id">
                        <div class="card mb-4">
                            <div class="card-body">
                                <div class="alert alert-info">Save the Inspection Form First to start adding Custom Fields.</div>
                            </div>
                        </div>
                    </template>
                    <template v-else>
                        <custom-field-builder ref="inspFormCustomFieldBuilder" :starting_sections="custom_field_sections" :show_save="false"></custom-field-builder>
                    </template>
                </div>
                <div class="col-xl-4">
                    <div class="card">
                        <div class="card-header">Sections ({{ form.sections.length }})</div>
                        <div class="card-body">
                            <div v-for="section in form.sections">
                                <strong>{{ section.DisplayOrder }}. </strong>{{ section.SectionName }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        data: function() {
            return {
                loading: true,
                saving: false,
                inspection_form_id: this.$route.params.id,
                form: {},
                errors: {},
                custom_field_sections: [],
                result_labels: {
                    compliant: null,
                    noncompliant: null,
                    na: null,
                },
                personnel: [],
            }
        },

        computed: {

        },

        methods: {

            getForm() {
                //Get the data
                this.$http
                    .get('inspections/forms/edit' + (this.inspection_form_id ? '/'+this.inspection_form_id : ''))
                    .then(response => {
                        //Set all the data
                        this.form = response.data.form;
                        this.personnel = response.data.personnel;

                        //Check for Result labels
                        if(this.form.result_labels)
                        {
                            this.result_labels = this.form.result_labels;
                        }

                        //Check for it
                        if(response.data.custom_field_sections)
                        {
                            this.custom_field_sections = response.data.custom_field_sections;
                        }
                    })
                    .catch(response => {

                    })
                    .then(() => {
                        //Set loading
                        this.loading = false;
                    });
            },

            saveForm(){
                //Setup
                this.saving = true;
                this.errors = {};

                //Set the Labels
                this.form.result_labels = this.result_labels;
                this.form.personnel = this.$refs.inspPersonnelFields.getPersonnel();

                //Update the errors for the Custom Field Builder
                if(this.form.inspection_form_id)
                {
                    //Reset
                    this.$refs.inspFormCustomFieldBuilder.setErrors([]);

                    //Setup
                    this.form.sections = this.$refs.inspFormCustomFieldBuilder.getSections();
                }

                //Get the data
                this.$http
                    .post('inspections/forms', this.form)
                    .then(response => {
                        //Alert
                        this.$toastr.success('Inspection Form Saved');

                        //Reload to the edit page
                        if(!this.inspection_form_id)
                        {
                            this.$router.push('/inspections/forms/edit/' + response.data.inspection_form_id);
                        }
                        else
                        {
                            this.$router.push('/inspections/forms/view/' + response.data.inspection_form_id);
                        }
                    })
                    .catch(error => {
                        //Check for error
                        if(error.response.status === 422)
                        {
                            //Display validation errors
                            this.errors = error.response.data;

                            //Update the errors for the Custom Field Builder
                            if(this.form.inspection_form_id)
                            {
                                //Share the errors
                                this.$refs.inspFormCustomFieldBuilder.setErrors(this.errors);
                            }
                        }
                        else
                        {
                            //Alert the user
                            this.$toastr.error(error.response.data, 'Failed to Create Inspection Form');
                        }
                    })
                    .then(() => {
                        //Svaing
                        this.saving = false;
                    });
            },

            newSection(){
                //Build the section
                var section = {
                    inspection_form_id: this.inspection_form_id,
                    IsActive: 1,
                    DisplayOrder: (this.form.sections.length + 1),
                }

                //Setter
                this.$refs.sectionModal.setSection(section);
            },

            editSection(section){
                //Setter
                this.$refs.sectionModal.setSection(section);
            },

            deleteSection(section){
                //Delete it
                if(!confirm('Are you sure you want to delete this section?'))
                {
                    return;
                }

                //Send it
                this.$http
                .post('inspections/forms/section/delete/'+section.InspectionSectionId)
                .then(response => {
                    //Alert the user?
                    this.$toastr.success('Form Section Deleted');

                    //Emit the Data
                    //this.$emit('elementSaved', response.data);

                    //Setter
                    this.form.sections = response.data;
                })
                .catch(error => {
                    //Check for error
                    this.$toastr.error(error.response.data);
                })
                .then(response => {
                    //ANything?
                });
            },

            markAll(sectionId, activeOrInactive){
                //Delete it
                if(!confirm('Are you sure you want to mark all items in this section '+(activeOrInactive == 1 ? 'Active' : 'Inactive')+'?'))
                {
                    return;
                }

                //Send it
                this.$http
                .post('inspections/forms/section/active/'+sectionId, {active: activeOrInactive})
                .then(response => {
                    //Alert the user?
                    this.$toastr.success('Form Section Items ' + (activeOrInactive == 1 ? 'Activated' : 'Deactivated'));

                    //Update Each Element?
                    for (var i = 0; i < this.form.sections.length; i++)
                    {
                        //Check ID
                        if(this.form.sections[i].InspectionSectionId != sectionId)
                        {
                            continue;
                        }

                        //Loop through Elements
                        for (var j = 0; j < this.form.sections[i].elements.length; j++)
                        {
                            //Set is active
                            this.form.sections[i].elements[j].IsActive = activeOrInactive;
                        }
                    }
                })
                .catch(error => {
                    //Check for error
                    this.$toastr.error(error.response.data);
                })
                .then(response => {
                    //ANything?
                });
            },

            sectionSaved(sections, deleteMode = false){
                // //Upsert the sections
                // for(var i in this.form.sections)
                // {
                //     //Check ID
                //     if(section.InspectionSectionId == this.form.sections[i].InspectionSectionId)
                //     {
                //         //Update or Delete?
                //         if(typeof deleteMode != 'undefined' && deleteMode == true)
                //         {
                //             this.form.sections.splice(i, 1);
                //             return;
                //         }

                //         //Set the value
                //         this.form.sections[i].SectionName = section.SectionName;
                //         this.form.sections[i].DisplayOrder = section.DisplayOrder;
                //         this.form.sections[i].IsActive = section.IsActive;
                //         this.form.sections[i].Description = section.Description;
                //         //this.form.sections.splice(i, 1, section);

                //         //Stop here;
                //         return;
                //     }
                // }

                //If it gets here, it's a new bid. Push it
                //this.form.sections.push(section);
                this.form.sections = sections;

                this.$forceUpdate();
            },

            newElement(InspectionSectionId){
                //SEtup
                var DisplayOrder = '';

                //Get the section
                for(var i in this.form.sections)
                {
                    //Check ID
                    if(InspectionSectionId == this.form.sections[i].InspectionSectionId)
                    {
                        //Check for undefined if this was a recently created element.
                        if(this.form.sections[i].elements === undefined)
                        {
                            var elements = [];
                        }
                        else
                        {
                            var elements = this.form.sections[i].elements;
                        }

                        DisplayOrder = elements.length + 1;
                    }
                }

                //Build the section
                var element = {
                    InspectionSectionId: InspectionSectionId,
                    IsActive: 1,
                    DisplayOrder: DisplayOrder,
                    element_score: 1,
                }

                //Setter
                this.$refs.elementModal.setElement(element);
            },

            editElement(element){
                //Setter
                this.$refs.elementModal.setElement(element);
            },

            deleteElement(element){
                //Delete it
                if(!confirm('Are you sure you want to delete this element?'))
                {
                    return;
                }

                //Send it
                this.$http
                .post('inspections/forms/element/delete/'+element.InspectionElementId)
                .then(response => {
                    //Alert the user?
                    this.$toastr.success('Form Element Deleted');

                    //Emit the Data
                    //this.$emit('elementSaved', response.data);

                    //Rebuild the Section
                    for (var i = this.form.sections.length - 1; i >= 0; i--)
                    {
                        //Check it
                        if(this.form.sections[i].InspectionSectionId == response.data.InspectionSectionId)
                        {
                            //Replace the elements
                            this.form.sections[i].elements = response.data.elements;

                            //Stop
                            return;
                        }
                    }
                })
                .catch(error => {
                    //Check for error
                    this.$toastr.error(error.response.data);
                })
                .then(response => {
                    //ANything?
                });
            },

            elementSaved(payload){
                //Set it
                var elements = payload.elements;
                var inspectionSectionId = payload.InspectionSectionId;

                //Upsert the sections
                for(var i in this.form.sections)
                {
                    //Check ID
                    if(inspectionSectionId == this.form.sections[i].InspectionSectionId)
                    {
                        //Set elements
                        this.form.sections[i].elements = elements;

                        //Force it
                        this.$forceUpdate();
                    }
                }
            },
        },

        mounted() {
            //Get it
            this.getForm();
        }
    }
</script>